/**
 * Pivot tile body (S5) — `pivotTransform` rendered as a compact grid with a
 * sticky first column + header. Row/column/grand totals are styled distinctly
 * (muted background, semibold) so they read as summary rows, never as data.
 * Fed by `useTileResult` (the single data hook); designed states come from
 * `QueryTileStates` (shared with S3/S4 tile views).
 */
import { useMemo } from 'react';
import type { PivotTile } from '../../types/dashboardTypes';
import { useTileResult } from '../../hooks/useTileResult';
import { pivotTransform, resolvePivotTileRefs } from '../../utils/pivotTransform';
import { analyzeColumns } from '../../../charts/utils/columnTypeDetection';
import { QueryTileStates } from './QueryTileStates';
import { tileEmptyText } from './tileEmptyText';

const formatCell = (value: number | null): string =>
  value === null ? '∅' : value.toLocaleString();

export function PivotTileView({ dashboardId, tile }: { dashboardId: string; tile: PivotTile }) {
  const state = useTileResult(dashboardId, tile);
  const rows = state.result?.selected.rows;

  const pivot = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    // Resolves the persisted rowColumn/columnColumn/valueColumn against the
    // CURRENT result columns — an older tile's NAME-based (or whitespace-
    // rekeyed) reference reads the right column instead of an empty pivot
    // (PR review, P2 companion to the chart/stat-tile fix).
    const resolved = resolvePivotTileRefs(
      { rowColumn: tile.rowColumn, columnColumn: tile.columnColumn, valueColumn: tile.valueColumn },
      analyzeColumns(state.result?.selected.columns ?? [], rows),
    );
    return pivotTransform(rows, { ...resolved, aggregation: tile.aggregation });
  }, [rows, state.result?.selected.columns, tile.rowColumn, tile.columnColumn, tile.valueColumn, tile.aggregation]);

  return (
    <QueryTileStates state={state}>
      {pivot && pivot.rows.length > 0 ? (
        <div className="flex h-full min-h-0 flex-col" data-testid="pivot-tile-body">
          {pivot.truncated && (
            <div
              className="border-b border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400"
              data-testid="pivot-truncated-banner"
            >
              Showing the first 50 distinct columns — more exist for this data.
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full border-collapse text-xs" data-testid="pivot-tile-table">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-20 whitespace-nowrap border-b border-r border-border bg-card px-2 py-1 text-left font-medium text-muted-foreground">
                    {tile.rowColumn}
                  </th>
                  {pivot.columnKeys.map((col) => (
                    <th
                      key={col}
                      className="sticky top-0 z-10 whitespace-nowrap border-b border-border bg-card px-2 py-1 text-right font-medium text-muted-foreground"
                    >
                      {col}
                    </th>
                  ))}
                  <th
                    className="sticky top-0 z-10 whitespace-nowrap border-b border-border bg-muted/60 px-2 py-1 text-right font-semibold text-muted-foreground"
                    data-testid="pivot-total-header"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {pivot.rows.map((row) => (
                  <tr key={row.rowKey} data-testid="pivot-tile-row">
                    <th className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-card px-2 py-1 text-left font-normal">
                      {row.rowKey}
                    </th>
                    {row.cells.map((cell, i) => (
                      <td
                        key={pivot.columnKeys[i]}
                        className="whitespace-nowrap px-2 py-1 text-right"
                      >
                        {cell === null ? (
                          <span className="text-muted-foreground">{formatCell(cell)}</span>
                        ) : (
                          formatCell(cell)
                        )}
                      </td>
                    ))}
                    <td
                      className="whitespace-nowrap bg-muted/40 px-2 py-1 text-right font-semibold"
                      data-testid="pivot-row-total"
                    >
                      {row.total.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="sticky left-0 bottom-0 z-10 whitespace-nowrap border-r border-t border-border bg-muted/60 px-2 py-1 text-left font-semibold text-muted-foreground">
                    Total
                  </th>
                  {pivot.columnTotals.map((total, i) => (
                    <td
                      key={pivot.columnKeys[i]}
                      className="whitespace-nowrap border-t border-border bg-muted/40 px-2 py-1 text-right font-semibold"
                      data-testid="pivot-column-total"
                    >
                      {total.toLocaleString()}
                    </td>
                  ))}
                  <td
                    className="whitespace-nowrap border-t border-border bg-muted/60 px-2 py-1 text-right font-semibold"
                    data-testid="pivot-grand-total"
                  >
                    {pivot.grandTotal.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div
          className="flex h-full items-center justify-center text-xs text-muted-foreground"
          data-testid="pivot-tile-empty"
        >
          {tileEmptyText(tile, 'No data to display')}
        </div>
      )}
    </QueryTileStates>
  );
}
